import { useCallback, useEffect, useState } from 'react'
import { api, errText, type FeedRow, type FriendRow } from '../api'
import { relativeDay } from '../lib/dates'
import { copyText, shareText } from '../lib/clipboard'
import { inviteLink } from '../lib/invite'
import { shareInvite } from '../max'
import { T } from '../strings'
import type { Theme } from '../theme'
import type { GameState } from '../game/types'
import type { GameStore } from '../game/store'
import { masteredPct, stickers } from '../game/selectors'
import Toast from '../components/Toast'

interface Props {
  state: GameState
  theme: Theme
  store: GameStore
}

const MEDALS = ['🥇', '🥈', '🥉']

/** Голая строка цифр — это телефон, всё остальное — код приглашения. */
function asPayload(raw: string): { code?: string; phone?: string } {
  const digits = raw.replace(/\D/g, '')

  return digits.length >= 10 && !/[А-Яа-яA-Za-z]/.test(raw) ? { phone: digits } : { code: raw }
}

export default function Friends({ state, theme, store }: Props) {
  const [rows, setRows] = useState<FriendRow[] | null>(null)
  const [feed, setFeed] = useState<FeedRow[]>([])
  const [input, setInput] = useState('')
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const code = state.account?.inviteCode ?? ''
  const botName = state.account?.botName ?? ''
  const link = code ? inviteLink(code, botName) : ''
  const inviteText = `Давай соревноваться в таблице умножения! Мой код: ${code}`

  const reload = useCallback(async () => {
    try {
      const [friends, events] = await Promise.all([api.friends(), api.feed()])
      setRows(friends.friends)
      setFeed(events.feed)
    } catch {
      // Сети нет — показываем хотя бы свою строку из локального прогресса.
      setRows(null)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload, state.account?.id, state.inviteNote])

  const addFriend = async () => {
    const value = input.trim()
    if (!value || busy) return

    setBusy(true)
    setNote(null)
    try {
      const { friend } = await api.addFriend(asPayload(value))
      setInput('')
      setNote(T.friendAdded(friend.name))
      await reload()
    } catch (error) {
      setNote(errText(error, 'Не получилось добавить друга'))
    } finally {
      setBusy(false)
    }
  }

  const removeFriend = async (id: string) => {
    if (!window.confirm(T.removeConfirm)) return
    try {
      await api.removeFriend(id)
      await reload()
    } catch (error) {
      setNote(errText(error, 'Не получилось убрать друга'))
    }
  }

  const copy = async (text: string) => {
    setNote((await copyText(text)) ? T.copied : T.copyFailed)
  }

  /** Сначала штатное «поделиться» мессенджера или системы, иначе просто копия. */
  const share = async () => {
    if (await shareInvite(inviteText, link)) return
    if (await shareText(inviteText, link)) return
    await copy(link)
  }

  // Оффлайн-заглушка: одна своя строка, собранная из локального состояния.
  const list: FriendRow[] = rows ?? [
    {
      id: state.account?.id ?? 'me',
      name: T.you,
      avatarUrl: state.profile.avatarUrl,
      pct: masteredPct(state),
      streak: state.progress.streak,
      stickers: stickers(state, theme).unlocked,
      lastPlayed: state.progress.lastPlayed,
      isMe: true,
    },
  ]

  return (
    <div className="screen screen--pad">
      <h1 className="screen__title">{T.friendsTitle}</h1>
      <p className="screen__sub">{T.friendsSub}</p>

      {state.inviteNote && (
        <div className="invite-note" style={{ borderColor: theme.acc, color: theme.acc }}>
          <span>{state.inviteNote}</span>
          <button type="button" className="invite-note__close" aria-label="Закрыть" onClick={store.dismissInviteNote}>
            ×
          </button>
        </div>
      )}

      {!state.profile.firstName && (
        <button type="button" className="name-hint" onClick={() => store.go('profile')}>
          {T.namePrompt}
        </button>
      )}

      <div className="friends">
        {list.map((row, i) => (
          <div
            key={row.id}
            className="friend-row"
            style={{
              borderColor: row.isMe ? theme.acc : theme.line,
              background: row.isMe ? theme.card : 'transparent',
            }}
          >
            <div className="friend-row__rank">{MEDALS[i] ?? i + 1}</div>
            <div className="friend-row__avatar" aria-hidden>
              {row.avatarUrl ? <img src={row.avatarUrl} alt="" /> : '🐼'}
            </div>
            <div className="friend-row__body">
              <div className="friend-row__name">{row.isMe ? T.you : row.name}</div>
              <div className="friend-row__stats">
                🔥 {row.streak} · 🏅 {row.stickers}
              </div>
            </div>
            <div className="friend-row__pct" style={{ color: row.isMe ? theme.acc : theme.ink }}>
              {row.pct}%
            </div>
            {!row.isMe && (
              <button
                type="button"
                className="friend-row__remove"
                aria-label={T.remove}
                onClick={() => void removeFriend(row.id)}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {list.length === 1 && <p className="empty">{T.friendsEmpty}</p>}

      <div className="invite">
        <div className="invite__title">{T.inviteTitle}</div>
        <div className="invite__ways">{T.inviteWays}</div>

        <div className="invite__step">{T.inviteWayCode}</div>
        <div className="invite__code" style={{ color: theme.acc, borderColor: theme.line }}>
          {code || '······'}
        </div>
        <button
          type="button"
          className="btn-3d btn-scale invite__btn"
          disabled={!code}
          style={{ background: theme.acc, color: theme.onacc }}
          onClick={() => void copy(code)}
        >
          📋 {T.copyCode}
        </button>
        <div className="invite__hint">{T.inviteHint}</div>

        <div className="invite__step">{T.inviteWayLink}</div>
        <button
          type="button"
          className="btn-ghost btn-scale invite__btn"
          disabled={!code}
          onClick={() => void share()}
        >
          🔗 {T.shareLink}
        </button>
        <div className="invite__hint">{T.inviteLinkHint}</div>
      </div>

      <div className="add-friend">
        <div className="invite__step">{T.haveCode}</div>
        <div className="invite__hint">{T.haveCodeHint}</div>
        <div className="add-friend__row">
          <input
            className="input"
            type="text"
            value={input}
            placeholder={T.friendPlaceholder}
            maxLength={20}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addFriend()
            }}
          />
          <button
            type="button"
            className="btn-3d btn-scale"
            disabled={busy || !input.trim()}
            style={{ background: theme.acc, color: theme.onacc }}
            onClick={() => void addFriend()}
          >
            {T.addFriend}
          </button>
        </div>
      </div>

      <Toast text={note} onHide={() => setNote(null)} />

      <div className="label" style={{ marginTop: 22 }}>
        {T.feed}
      </div>
      <div className="feed">
        {feed.length === 0 && <p className="empty">{T.feedEmpty}</p>}
        {feed.map((item) => (
          <div key={item.id} className="feed-item">
            <div className="feed-item__avatar" aria-hidden>
              {item.avatarUrl ? <img src={item.avatarUrl} alt="" /> : '🐼'}
            </div>
            <div>
              <div className="feed-item__text">
                <b>{item.name}</b> {item.text}
              </div>
              <div className="feed-item__when">{relativeDay(item.createdAt)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
